import { computed, inject } from '@angular/core';
import { QueryParamStateService } from '../../services/query-param-state.service';
import { getHuidigeMaand, getMaandLabelLangVoorlopig, getSchooljaarTovHuidig, today } from '@cumlaude/shared-utils';
import { Option } from '@cumlaude/shared-components-inputs';
import { FilterService } from '../../services/filter.service';
import { toSignal } from '@angular/core/rxjs-interop';

import { FilterName } from '../../services/filter-name';

/**
 * Helper class voor de opties en state van een maand-dropdown die gekoppeld is aan een query param.
 * Naast de maanden (value = 1-12) is "Hele schooljaar" beschikbaar als optie (value = 0).
 *
 * De opties en default-waarde zijn afhankelijk van het schooljaar-filter:
 * - Huidig schooljaar: default is vorige maand en opties is augustus t/m vorige maand
 * - Anders: default is "Hele schooljaar" (0) en opties is augustus t/m juli
 *
 * Gebruik:
 * ```
 * maandDropdownState = new MaandDropdownState('my-maand-param');
 *
 * <app-single-select-dropdown
 *     [options]="maandDropdownState.opties()"
 *     [selectedOptie]="maandDropdownState.selectedOptie()"
 *     (onOptieClicked)="maandDropdownState.setMaand($event)"
 *
 * const gekozenMaand = this.maandDropdownState.maandMetFallback(); // 1-12; 0 = Hele schooljaar
 * ```
 * />
 */
export class MaandDropdownState {
	constructor(
		private readonly paramName: 'lesuur-maand' | 'reden-maand',
		private readonly schooljaarFilter: FilterName = 'aw_nm_schooljaar'
	) {}

	private readonly filterService = inject(FilterService);
	private readonly qp = inject(QueryParamStateService);
	private readonly schooljaar = toSignal(this.filterService.observeAsInput(this.schooljaarFilter));

	/** Gekozen maand; default = undefined */
	maand = this.qp.signal(this.paramName);

	/** Gekozen maand; default = vorige maand (1-12) in huidig schooljaar, anders hele schooljaar (0) */
	maandMetFallback = computed(() => {
		const m = this.maand();
		if (m !== undefined) return m;

		return this.schooljaar() === getSchooljaarTovHuidig() ? getHuidigeMaand() : 0;
	});

	/** Wijzigt query param naar gekozen maand, maakt leeg indien default is gekozen */
	setMaand(maand: number) {
		const huidigSchooljaar = this.schooljaar() === getSchooljaarTovHuidig();
		if ((huidigSchooljaar && maand === getHuidigeMaand()) || (!huidigSchooljaar && maand === 0)) {
			// Standaard waarde, dus query param verwijderen
			this.qp.dispatch(this.paramName, undefined);
		} else {
			this.qp.dispatch(this.paramName, maand);
		}
	}

	/**
	 * De opties voor de dropdown:
	 * - "Hele schooljaar"
	 * - Indien huidig schooljaar: Augustus t/m huidige maand, anders Augustus t/m Juli (aflopend gesorteerd)
	 */
	opties = computed<Option<number>[]>(() => {
		// Zorg dat we in het huidige schooljaar niet verder gaan dan de huidige maand
		// getMonth is 0-based dus aug => (7+5)%12+1 = 1 maand, jul => (6+5)%12+1 = 12 maanden
		const length = this.schooljaar() === getSchooljaarTovHuidig() ? ((today().getMonth() + 5) % 12) + 1 : 12;
		// Genereer opties voor 0 = hele jaar + het berekende aantal maanden terug tot aug
		return [...Array.from({ length }, (_, i) => ((i + 7) % 12) + 1), 0].reverse().map((m) => ({
			text: m ? getMaandLabelLangVoorlopig(this.schooljaar(), m) : 'Hele schooljaar',
			value: m,
		}));
	});

	selectedOptie = computed<Option<number>>(() => this.opties().find((m) => m.value === this.maandMetFallback())!);

	selectedOptieLabel = computed<string>(() => this.selectedOptie().text);

	getMaandMetJaar(schooljaar: string = getSchooljaarTovHuidig()): string {
		const maand = this.maandMetFallback();
		if (maand === 0) {
			return `Schooljaar ${schooljaar}`;
		} else {
			return getMaandLabelLangVoorlopig(schooljaar, maand, true);
		}
	}
}
